import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { DexResultView } from '@mark43/dex-api';
import { css, HStack, Icon, styled, MenuListItem, Text } from '@cad-arc';
import { getArcHitColor, isCandidatesList, isDrilldownResult, isHit } from '../utils';
import { DexResultItemTrailingVisual } from './dex-result-item-trailing-visual';
import { UnreadIndicator } from './unread-indicator';
import { colors } from '../../colors/colors';

export const DexResultMenuListItem = styled(MenuListItem)<{
    $isSelected: boolean;
    $isHit: boolean;
}>`
    color: ${(props) => getArcHitColor(props.$isHit)};
    font-weight: ${(props) =>
        props.$isHit ? 'var(--arc-fontWeights-bold)' : 'var(--arc-fontWeights-normal)'};
    ${(props) =>
        props.$isSelected &&
        css`
            /* Can remove this focus declaration when ARC updates the MenuListItem styles. */
            &:hover,
            &:focus {
                background-color: var(--arc-colors-selected-accent);
                color: var(--arc-colors-selected-default);
            }
            background-color: var(--arc-colors-selected-accent);
            color: var(--arc-colors-selected-default);
        `};
    ${(props) =>
        !props.$isSelected &&
        props.$isHit &&
        css`
            &:hover,
            &:focus {
                color: ${colors.dexHit};
            }
        `};
    margin-bottom: var(--arc-space-1);
`;

function getResultLabel(result: DexResultView, dexUseResponseTypeAsResultLabel: boolean) {
    if (isCandidatesList(result) || isDrilldownResult(result)) {
        return result.responseType;
    } else {
        return dexUseResponseTypeAsResultLabel
            ? result.responseType
            : `${result.underlyingSource ?? ''} ${result.resultType ?? ''}`;
    }
}

const MAX_NESTING_LEVEL = 5;

export function DexResultItem({
    result,
    isSelected,
    nestingLevel,
    onClick,
}: {
    result: DexResultView;
    isSelected: boolean;
    nestingLevel: number;
    onClick: () => void;
}): React.ReactElement {
    const { dexUseResponseTypeAsResultLabel } = useEnabledFeatures();
    const label = getResultLabel(result, dexUseResponseTypeAsResultLabel);
    const descenderIconMarginPx = (Math.min(nestingLevel, MAX_NESTING_LEVEL) - 1) * 14;
    return (
        <DexResultMenuListItem
            tabIndex={-1}
            data-result-id={result.incomingMessageId}
            key={result.incomingMessageId}
            onClick={onClick}
            leadingVisual={<UnreadIndicator isRead={result.isRead} isHit={isHit(result)} />}
            trailingVisual={
                <DexResultItemTrailingVisual dexResult={result} isSelected={isSelected} />
            }
            $isSelected={isSelected}
            $isHit={isHit(result)}
        >
            <HStack>
                {nestingLevel > 0 && (
                    <Icon
                        icon="Descender"
                        color={isSelected ? 'brand.default' : 'icon.default'}
                        style={{ marginLeft: `${descenderIconMarginPx}px` }}
                    />
                )}
                <Text color="inherit" noOfLines={1}>
                    {label}
                </Text>
            </HStack>
        </DexResultMenuListItem>
    );
}
